import React, { useEffect, useMemo, useState } from 'react';
import { FiClock } from 'react-icons/fi';
import Dropdown from '../Dropdown/Dropdown';
import '../Dropdown/Dropdown.css';
import {
  AUDIO_SLEEP_HOUR_OPTIONS,
  AUDIO_SLEEP_MINUTE_OPTIONS,
  AUDIO_SLEEP_TIMER_MODES,
} from './useAudioSleepTimer';

const sleepDropdownProps = {
  menuPortal: true,
  menuPortalMinWidth: 120,
  menuPortalZIndex: 3100,
  menuPortalClassName: 'reusable-dropdown-menu--audio-sleep-timer',
};

function sleepStatusLine(sleepTimer) {
  if (!sleepTimer.isActive || !sleepTimer.remainingLabel) return '';
  if (sleepTimer.timerMode === 'end-track') return 'After this song';
  if (sleepTimer.timerMode === 'end-queue') return 'When queue ends';
  if (sleepTimer.timerMode === 'duration') {
    return `In ${sleepTimer.remainingLabel}`;
  }
  return '';
}

export default function AudioPlayerSleepTimerControls({ sleepTimer, className = '', embedded = false }) {
  const [expanded, setExpanded] = useState(embedded);
  const [hours, setHours] = useState('0');
  const [minutes, setMinutes] = useState('15');
  const [customError, setCustomError] = useState('');

  const showCustomFields = sleepTimer.timerMode === 'custom';

  useEffect(() => {
    if (sleepTimer.timerMode === 'off') {
      setCustomError('');
    }
  }, [sleepTimer.timerMode]);

  const modeOptions = useMemo(() => AUDIO_SLEEP_TIMER_MODES, []);

  const handleModeSelect = (opt) => {
    const value = opt?.value ?? 'off';
    setCustomError('');
    sleepTimer.setSleepTimerMode(value);
  };

  const handleStartCustom = () => {
    setCustomError('');
    const ok = sleepTimer.startDurationTimer(Number(hours), Number(minutes), 'custom');
    if (!ok) {
      setCustomError('Pick at least 1 minute.');
    }
  };

  const statusText = useMemo(() => sleepStatusLine(sleepTimer), [
    sleepTimer.isActive,
    sleepTimer.remainingLabel,
    sleepTimer.timerMode,
  ]);

  const wrapClass = ['audio-player__sleep-wrap', embedded ? 'audio-player__sleep-wrap--embedded' : '', className]
    .filter(Boolean)
    .join(' ');

  if (embedded) {
    return (
      <div className={wrapClass}>
        <div id="audio-player-sleep-panel" className="audio-player__sleep audio-player__sleep--embedded">
          <div className="audio-player__sleep-dropdown audio-player__sleep-dropdown--mode" aria-label="Sleep timer">
            <Dropdown
              options={modeOptions}
              selectedOption={sleepTimer.dropdownValue}
              onSelect={handleModeSelect}
              menuPortal
              menuPortalMinWidth={200}
              menuPortalZIndex={3100}
              menuPortalClassName="reusable-dropdown-menu--audio-sleep-timer"
            />
          </div>

          {showCustomFields ? (
            <>
              <div className="audio-player__sleep-dropdown audio-player__sleep-dropdown--unit" aria-label="Hours">
                <Dropdown
                  options={AUDIO_SLEEP_HOUR_OPTIONS}
                  selectedOption={hours}
                  onSelect={(opt) => setHours(opt?.value ?? '0')}
                  {...sleepDropdownProps}
                />
              </div>
              <div className="audio-player__sleep-dropdown audio-player__sleep-dropdown--unit" aria-label="Minutes">
                <Dropdown
                  options={AUDIO_SLEEP_MINUTE_OPTIONS}
                  selectedOption={minutes}
                  onSelect={(opt) => setMinutes(opt?.value ?? '0')}
                  {...sleepDropdownProps}
                />
              </div>
              <button type="button" className="audio-player__sleep-start-btn" onClick={handleStartCustom}>
                Start
              </button>
            </>
          ) : null}

          {statusText ? (
            <span className="audio-player__sleep-countdown" role="status" title="Sleep timer">
              {statusText}
            </span>
          ) : null}

          {customError ? (
            <span className="audio-player__sleep-custom-error" role="alert">
              {customError}
            </span>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className={wrapClass}>
      <div className="audio-player__sleep-header">
        <button
          type="button"
          className="audio-player__sleep-toggle"
          aria-expanded={expanded}
          aria-controls="audio-player-sleep-panel"
          onClick={() => setExpanded((open) => !open)}
        >
          <FiClock aria-hidden />
          <span>Sleep timer</span>
          <span className="audio-player__sleep-toggle-chevron" aria-hidden>
            {expanded ? '▾' : '▸'}
          </span>
        </button>
        {!expanded && statusText ? (
          <span className="audio-player__sleep-countdown audio-player__sleep-countdown--header" role="status">
            {statusText}
          </span>
        ) : null}
      </div>

      {expanded ? (
        <div id="audio-player-sleep-panel" className="audio-player__sleep">
          <div className="audio-player__sleep-dropdown audio-player__sleep-dropdown--mode" aria-label="Sleep timer">
            <Dropdown
              options={modeOptions}
              selectedOption={sleepTimer.dropdownValue}
              onSelect={handleModeSelect}
              menuPortal
              menuPortalMinWidth={200}
              menuPortalZIndex={3100}
              menuPortalClassName="reusable-dropdown-menu--audio-sleep-timer"
            />
          </div>

          {showCustomFields ? (
            <>
              <div className="audio-player__sleep-dropdown audio-player__sleep-dropdown--unit" aria-label="Hours">
                <Dropdown
                  options={AUDIO_SLEEP_HOUR_OPTIONS}
                  selectedOption={hours}
                  onSelect={(opt) => setHours(opt?.value ?? '0')}
                  {...sleepDropdownProps}
                />
              </div>
              <div className="audio-player__sleep-dropdown audio-player__sleep-dropdown--unit" aria-label="Minutes">
                <Dropdown
                  options={AUDIO_SLEEP_MINUTE_OPTIONS}
                  selectedOption={minutes}
                  onSelect={(opt) => setMinutes(opt?.value ?? '0')}
                  {...sleepDropdownProps}
                />
              </div>
              <button type="button" className="audio-player__sleep-start-btn" onClick={handleStartCustom}>
                Start
              </button>
            </>
          ) : null}

          {statusText ? (
            <span className="audio-player__sleep-countdown" role="status" title="Sleep timer">
              {statusText}
            </span>
          ) : null}

          {customError ? (
            <span className="audio-player__sleep-custom-error" role="alert">
              {customError}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
